import type { Timestamp } from 'firebase/firestore';

/**
 * A card marked as starred/favorite by the user.
 * Tracked by oracle_id for version-agnostic starring.
 * Optionally stores scryfall_id for specific printing display.
 */
export interface StarredCard {
  oracle_id: string;
  name: string;
  scryfall_id?: string; // Specific printing used when starring
  starred_at: Timestamp;
}

// ============================================================================
// Lists
// ============================================================================

/**
 * A user-created list for tracking cards (wishlists, want lists, trade lists, etc.).
 * Stored at: users/{uid}/lists/{listId}
 */
export interface CardList {
  id: string;
  name: string;
  color: string | null; // Hex color for UI
  icon: string | null; // Emoji or icon name
  card_count: number; // Denormalized for list display
  total_value: number; // Estimated total price
  created_at: Date;
  updated_at: Date;
}

/**
 * Priority level for list cards.
 */
export type ListPriority = 'high' | 'medium' | 'low';

/**
 * A card entry in a list.
 * Stored at: users/{uid}/list_cards/{cardId}
 * Uses flat structure for simpler queries across lists.
 */
export interface ListCard {
  id: string;
  list_id: string; // FK to lists
  oracle_id: string; // Groups all printings (version-agnostic)
  name: string; // Denormalized for display
  /** Copies the user wants to acquire/track. Owned copies are NOT deducted from this. */
  quantity: number;
  priority: ListPriority;
  notes: string | null; // Why you want this card
  added_at: Date;
  scryfall_id: string; // Specific edition (required)
}

// ============================================================================
// Enriched Types (with computed data)
// ============================================================================

/**
 * List card enriched with collection and price data.
 */
export interface EnrichedListCard extends ListCard {
  /** Copies owned of this exact printing. */
  owned_quantity: number;
  /** Copies owned of the same card in other printings. */
  owned_other_editions: number;
  /** Owned by name across all printings, minus copies committed to active decks. */
  available_quantity: number;
  /** Selected printing's price in the user's currency, null when uncached or unpriced. */
  unit_price: number | null;
  /** unit_price × quantity, null when the printing has no price. */
  total_price: number | null;
  sample_image_uris: { small: string; normal: string } | null;
}

/**
 * List enriched with computed statistics.
 */
export interface EnrichedCardList extends CardList {
  cards: EnrichedListCard[];
  /** Sum of `unit_price × quantity` over rows that have a price. */
  total_price: number;
  /** Rows whose printing has no cached price, so they are excluded from `total_price`. */
  unpriced_count: number;
}

// ============================================================================
// Deck Needs (Computed)
// ============================================================================

/**
 * A card needed across one or more decks.
 */
export interface DeckNeedCard {
  oracle_id: string;
  name: string;
  total_needed: number; // Across all decks
  owned: number;
  /** Copies still to acquire. Per-deck views also deduct copies committed to other active decks. */
  shortfall: number;
  decks: DeckNeedSource[]; // Which decks need this
  cheapest_price: number | null;
}

/**
 * Source deck for a needed card.
 */
export interface DeckNeedSource {
  deck_id: string;
  deck_name: string;
  quantity: number;
}

// ============================================================================
// Display Helpers
// ============================================================================

export const PRIORITY_LABELS: Record<ListPriority, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export const DEFAULT_LIST_ICONS = ['🛒', '📋', '🔄', '🎯', '💰', '🎁', '⭐', '🔥', '❤️', '📌'];
